/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { create } from 'zustand'
import {
  orchestrate,
  PeerResponse,
  JudgeResult,
} from '@/core/orchestrator'

// Re-export types from orchestrator for convenience
export type { PeerResponse, JudgeResult } from '@/core/orchestrator'

export interface MetricsDataPoint {
  timestamp: number
  cost: number
  latency: number
}

// Alias for backwards compatibility
export type MetricsEntry = MetricsDataPoint

interface OrchestratorState {
  peerResponses: Record<string, PeerResponse | null> // personaId -> response
  judgeResult: JudgeResult | null
  isOrchestrating: boolean
  totalTokens: number // Last orchestration tokens
  totalCost: number // Last orchestration cost
  latency: number
  metricsHistory: MetricsDataPoint[]
  cumulativeCost: number // Total cost across all orchestrations in this session
  cumulativeTokens: number // Total tokens across all orchestrations in this session
  error: any | null // Last error from orchestration

  // Actions
  runOrchestration: (userPrompt: string, peers: any[], rubric?: string, judgeModel?: string, contextFiles?: string, peerHistories?: Record<string, any[]>) => Promise<void>
  clearResponses: () => void
  updatePeerResponse: (response: PeerResponse) => void
  updateJudgeResult: (result: JudgeResult) => void
  loadPeerResponses: (peerResponses: Record<string, PeerResponse | null>, judgeResult: JudgeResult | null) => void
  clearError: () => void
  resetToDefault: () => void
}

export const useOrchestratorStore = create<OrchestratorState>((set, get) => ({
  peerResponses: {},
  judgeResult: null,
  isOrchestrating: false,
  totalTokens: 0,
  totalCost: 0,
  latency: 0,
  metricsHistory: [],
  cumulativeCost: 0,
  cumulativeTokens: 0,
  error: null,

  runOrchestration: async (userPrompt: string, peers: any[], rubric?: string, judgeModel?: string, contextFiles?: string, peerHistories?: Record<string, any[]>) => {
    console.log('[OrchestratorStore] runOrchestration called:', {
      promptLength: userPrompt?.length,
      promptPreview: userPrompt?.substring(0, 100) + '...',
      totalPeers: peers.length,
      enabledPeers: peers.filter(p => p.enabled).length,
      hasRubric: !!rubric,
      hasContextFiles: !!contextFiles,
      hasPeerHistories: !!peerHistories
    })

    // Clear previous responses for all peers
    const clearedResponses: Record<string, PeerResponse | null> = {}
    peers.filter(p => p.enabled).forEach(peer => {
      clearedResponses[peer.id] = null
    })

    set({
      isOrchestrating: true,
      peerResponses: clearedResponses,
      judgeResult: null,
      error: null, // Clear previous errors
    })

    try {
      // Convert project peers to personas format
      const personas = peers
        .filter(p => p.enabled)
        .map(peer => ({
          id: peer.id,
          name: peer.personaName,
          role: peer.role,
          provider: 'openrouter' as const,
          model: peer.model || 'anthropic/claude-sonnet-4.5',
          systemPrompt: peer.systemPrompt,
          color: peer.color,
          icon: peer.icon,
        }))

      console.log('[OrchestratorStore] Personas created:', personas.map(p => ({ id: p.id, name: p.name, model: p.model })))

      // Build peer histories for peers with preserveContext enabled
      const filteredPeerHistories: Record<string, any[]> = {}
      if (peerHistories) {
        peers.filter(p => p.enabled && p.preserveContext).forEach(peer => {
          if (peerHistories[peer.id]) {
            filteredPeerHistories[peer.id] = peerHistories[peer.id]
            console.log(`[Orchestrator] Peer ${peer.personaName} will receive ${peerHistories[peer.id].length} messages of conversation history`)
          }
        })
      }

      console.log('[OrchestratorStore] Calling orchestrate() with:', {
        personasCount: personas.length,
        hasRubric: !!rubric,
        judgeModel: judgeModel || 'anthropic/claude-sonnet-4.5',
        hasContextFiles: !!contextFiles,
        hasPeerHistories: Object.keys(filteredPeerHistories).length > 0
      })

      const result = await orchestrate(userPrompt, {
        personas,
        rubric,
        judgeModel: judgeModel || 'anthropic/claude-sonnet-4.5',
        contextFiles,
        peerHistories: Object.keys(filteredPeerHistories).length > 0 ? filteredPeerHistories : undefined,
        onPeerResponse: (response) => {
          console.log('[OrchestratorStore] onPeerResponse callback received:', {
            personaId: response.personaId,
            personaName: response.personaName,
            contentLength: response.content?.length,
            contentPreview: response.content?.substring(0, 100) + '...',
            tokens: response.tokens,
            cost: response.cost
          })
          get().updatePeerResponse(response)
        },
        onJudgeResult: (result) => {
          console.log('[OrchestratorStore] onJudgeResult callback received:', {
            score: result.score,
            approved: (result as any).approved
          })
          get().updateJudgeResult(result)
        },
      })

      console.log('[OrchestratorStore] orchestrate() completed, result:', {
        peerResponsesCount: result.peerResponses?.length,
        hasJudgeResult: !!result.judgeResult,
        totalTokens: result.totalTokens,
        totalCost: result.totalCost,
        latency: result.latency
      })

      // Add to metrics history
      const newDataPoint: MetricsDataPoint = {
        timestamp: Date.now(),
        cost: result.totalCost,
        latency: result.latency,
      }

      console.log('[OrchestratorStore] Orchestration result:', {
        totalTokens: result.totalTokens,
        totalCost: result.totalCost,
        peerCount: result.peerResponses.length,
        hasJudge: !!result.judgeResult
      })

      set((state) => {
        const newCumulativeCost = state.cumulativeCost + result.totalCost
        const newCumulativeTokens = state.cumulativeTokens + result.totalTokens

        console.log('[OrchestratorStore] Updating cumulative values:', {
          previousCumulativeCost: state.cumulativeCost,
          addingCost: result.totalCost,
          newCumulativeCost,
          previousCumulativeTokens: state.cumulativeTokens,
          addingTokens: result.totalTokens,
          newCumulativeTokens
        })

        return {
          totalTokens: result.totalTokens,
          totalCost: result.totalCost,
          latency: result.latency,
          isOrchestrating: false,
          metricsHistory: [...state.metricsHistory, newDataPoint].slice(-20), // Keep last 20 data points
          cumulativeCost: newCumulativeCost,
          cumulativeTokens: newCumulativeTokens,
        }
      })
    } catch (error) {
      console.error('Orchestration error:', error)
      set({ isOrchestrating: false, error })
    }
  },

  updatePeerResponse: (response: PeerResponse) => {
    console.log('[OrchestratorStore] updatePeerResponse called:', {
      personaId: response.personaId,
      personaName: response.personaName,
      hasContent: !!response.content,
      contentLength: response.content?.length
    })
    set((state) => ({
      peerResponses: {
        ...state.peerResponses,
        [response.personaId]: response,
      },
    }))
  },

  updateJudgeResult: (result: JudgeResult) => {
    set({ judgeResult: result })
  },

  clearResponses: () => {
    set({
      peerResponses: {
        gemini: null,
        claude: null,
        grok: null,
      },
      judgeResult: null,
      totalTokens: 0,
      totalCost: 0,
      latency: 0,
      metricsHistory: [],
    })
  },

  loadPeerResponses: (peerResponses: Record<string, PeerResponse | null>, judgeResult: JudgeResult | null) => {
    set({
      peerResponses: {
        gemini: peerResponses.gemini || null,
        claude: peerResponses.claude || null,
        grok: peerResponses.grok || null,
      },
      judgeResult,
    })
  },

  clearError: () => {
    set({ error: null })
  },

  resetToDefault: () => {
    set({
      peerResponses: {
        gemini: null,
        claude: null,
        grok: null,
      },
      judgeResult: null,
      isOrchestrating: false,
      totalTokens: 0,
      totalCost: 0,
      latency: 0,
      metricsHistory: [],
      error: null,
    })
  },
}))

